import React from 'react';
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion';

import blurredPostbox from '../../../assets/img/notification/postbox-blur.png';

import {
  Blurred,
  BlurWithLogin,
  Modal,
  ModalElement,
  SkeletonModule
} from '../../common';
import { ZipCodeModal } from '../../zip-code';
import NotificationList from '../NotificationList/index.troy';
import UploadFile from '../UploadFile';
import UploadFileModal from '../UploadFileModal';
import ResponseChannelForm from '../ResponseChannelForm';

import styles from './index.module.scss';

import {
  clientFilterOption,
  filterOptions,
  filterOptionsMap,
  internalNoteFilterOption
} from '../../../constants/post-box';
import {
  EMAIL_FIELD,
  SMS_FIELD,
  WHATS_APP_FIELD
} from '../../../constants/response-channel';

import BodySeparator from '../BodySeparator';
import PostBoxFilter from '../PostBoxFilter';
import UploadFileModalConfirmation from '../UploadFileModalConfirmation';
import { parsePhoneFromString } from '../../../utils/phone';

const makeFiltersFromFilterOptions = filterOptions =>
  filterOptions.reduce((acc, { name }) => ({ ...acc, [name]: false }), {});

export default class PostBox extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      notifications: null,
      hasFilter: false,

      searchStr: '',

      filterOptions: [],
      filters: {},
      chosenStatisticFilter: null,

      isUploadReady: false,
      filesInfo: null,
      submitDone: false,

      zipCodeValidationOpened: false
    };

    const { internal } = props;
    const componentFilterOptions = [...filterOptions];
    if (internal) {
      componentFilterOptions.push(internalNoteFilterOption);
      componentFilterOptions.push(clientFilterOption);
    }
    this.state.filterOptions = componentFilterOptions;

    this.state.filters = makeFiltersFromFilterOptions(this.state.filterOptions);
  }

  static getDerivedStateFromProps(props, state) {
    let returnObj = null;
    if (!state.hasFilter && !state.searchStr && props.notifications) {
      returnObj = {
        ...returnObj,
        notifications: props.notifications
      };
    }
    return returnObj;
  }

  componentDidUpdate(prevProps) {
    const {
      isFileUploadSuccessful: prevFileUploadSuccessful,
      zipCodeStatus: prevZipCodeStatus
    } = prevProps;
    const {
      isFileUploadSuccessful: curFileUploadSuccessful,
      zipCodeStatus: curZipCodeStatus
    } = this.props;

    if (
      prevFileUploadSuccessful !== curFileUploadSuccessful &&
      curFileUploadSuccessful
    ) {
      this.setState({
        submitDone: true,
        isUploadReady: false,
        filesInfo: null
      });
    }

    if (prevZipCodeStatus !== curZipCodeStatus && curZipCodeStatus) {
      this.onZipCodeFormClose();
    }
  }

  onFilterOptionChange = name => event => {
    this.setState(
      {
        filters: {
          ...this.state.filters,
          [name]: event.target.checked
        }
      },
      this.onFilterClick
    );
  };

  onSearchChange = event => {
    this.setState({ searchStr: event.target.value });
  };

  onTypeFilterChange = type => {
    this.setState(
      {
        chosenStatisticFilter: type
      },
      this.onFilterClick
    );
  };

  onSearchClear = () => {
    const { notifications } = this.props;

    this.setState({
      notifications,
      searchStr: '',
      hasFilter: false
    });
  };

  onFilterClear = () => {
    const { notifications } = this.props;

    this.setState({
      notifications,
      hasFilter: false,
      filters: makeFiltersFromFilterOptions(this.state.filterOptions),
      chosenStatisticFilter: null
    });
  };

  onAllFiltersClear = () => {
    const { notifications } = this.props;

    this.setState({
      notifications,
      hasFilter: false,
      filters: makeFiltersFromFilterOptions(this.state.filterOptions),
      searchStr: '',
      chosenStatisticFilter: null
    });
  };

  onSearchClick = () => {
    const { notifications } = this.props;
    const { searchStr } = this.state;
    const newNotifications = notifications.filter(
      ({ text }) =>
        text && text.toLowerCase().indexOf(searchStr.toLowerCase()) !== -1
    );
    this.setState({
      notifications: newNotifications,
      hasFilter: true
    });
  };

  onFilterClick = () => {
    const { notifications } = this.props;
    const { filters, chosenStatisticFilter } = this.state;

    this.setState({
      notifications: notifications.filter(({ channel, type }) => {
        var validChannel =
          //only filter if at least on channel is selected.
          !Object.values(filters).some(filterValue => filterValue) ||
          filters[filterOptionsMap[channel]];
        var validType =
          !chosenStatisticFilter || chosenStatisticFilter.includes(type);
        return validChannel && validType;
      }),
      hasFilter: true
    });
  };

  onUploadFileClick = () => {
    this.props.setFileModalOpened(true);
  };

  onUploadModalClose = () => {
    this.props.setFileModalOpened(false);
    this.setState({ isUploadReady: false, filesInfo: null, submitDone: false });
  };

  onResponseChannelSubmit = async values => {
    const { uploadFile } = this.props;
    const { filesInfo } = this.state;

    uploadFile({ ...values, ...filesInfo });
  };

  onUploadFileSubmit = values => {
    this.setState({ isUploadReady: true, filesInfo: values });
  };

  onBlurLoginClick = () => {
    this.setState({ zipCodeValidationOpened: true });
  };

  onZipCodeFormClose = () => {
    this.setState({ zipCodeValidationOpened: false });
  };

  onZipCodeSubmit = zipCode => {
    const { validateZipCode } = this.props;
    validateZipCode(zipCode);
  };

  render() {
    const {
      notifications,
      filters,
      isUploadReady,
      filterOptions,
      chosenStatisticFilter,
      submitDone,
      zipCodeValidationOpened
    } = this.state;

    const {
      token,
      groupCounts,
      fileUploadHidden,
      isFileLoading,
      isFileModalOpened,
      translations,
      mobilePhone,
      phone,
      email,
      whatsappDecision,
      fileUploadBlur,
      notificationsListBlur,
      prevalidateZipCode,
      isAuthenticatedAppLoading,
      zipCodeValidationEnabled,
      zipCodeAttempt,
      zipCodeMaxAttempts,
      zipCodeLoading
    } = this.props;

    if (isAuthenticatedAppLoading) {
      return (
        <div className={styles['postbox__body']}>
          <SkeletonModule topMargin type="widgets" />
          <SkeletonModule bottomMargin />
        </div>
      );
    }

    return (
      <div className="postbox__wrapper">
        <div className={styles['postbox__body']}>
          {!fileUploadHidden && (
            <Blurred blur={fileUploadBlur}>
              <UploadFile
                className={styles['postbox__upload-separator']}
                onButtonClick={this.onUploadFileClick}
              />
            </Blurred>
          )}
          <PostBoxFilter
            groups={groupCounts}
            onTypeChange={this.onTypeFilterChange}
            chosenStatisticFilter={chosenStatisticFilter}
            filterOptions={filterOptions}
            filterValues={filters}
            onOptionChange={this.onFilterOptionChange}
          />
          <BodySeparator />
          <BlurWithLogin
            blur={notificationsListBlur}
            mockImg={blurredPostbox}
            descriptionKey="POSTBOX_CENSORED"
            className={styles['postbox__blur-login-block']}
            buttonId="postbox.blur.logout-button"
            onButtonClick={
              zipCodeValidationEnabled ? this.onBlurLoginClick : null
            }
          >
            <NotificationList notifications={notifications} authToken={token} />
          </BlurWithLogin>
          {!fileUploadHidden && (
            <Modal
              withFullSizePaper
              isOpen={isFileModalOpened}
              onPaperClick={
                isUploadReady || submitDone
                  ? undefined
                  : this.onUploadModalClose
              }
            >
              {!isUploadReady && !submitDone && (
                <ModalElement>
                  <UploadFileModal
                    onClose={this.onUploadModalClose}
                    onSubmit={this.onUploadFileSubmit}
                    translations={translations}
                  />
                </ModalElement>
              )}
              <AnimateSharedLayout>
                <AnimatePresence>
                  {isUploadReady && !submitDone && (
                    <motion.div layoutId="upload">
                      <ModalElement>
                        <ResponseChannelForm
                          onSubmit={this.onResponseChannelSubmit}
                          initialValues={{
                            [SMS_FIELD]: parsePhoneFromString(
                              mobilePhone || phone
                            ),
                            [EMAIL_FIELD]: email,
                            [WHATS_APP_FIELD]: parsePhoneFromString(
                              mobilePhone || phone
                            )
                          }}
                          withWhatsapp={whatsappDecision}
                          isFileLoading={isFileLoading}
                          translations={translations}
                        />
                      </ModalElement>
                    </motion.div>
                  )}
                  {submitDone && (
                    <motion.div
                      layoutId="upload"
                      transition={{ duration: 0.3 }}
                    >
                      <ModalElement>
                        <UploadFileModalConfirmation
                          onClose={this.onUploadModalClose}
                        />
                      </ModalElement>
                    </motion.div>
                  )}
                </AnimatePresence>
              </AnimateSharedLayout>
            </Modal>
          )}
          {zipCodeValidationEnabled ? (
            <ZipCodeModal
              isOpen={zipCodeValidationOpened}
              onClose={this.onZipCodeFormClose}
              onSubmit={this.onZipCodeSubmit}
              attempt={zipCodeAttempt}
              maxAttempts={zipCodeMaxAttempts}
              isLoading={zipCodeLoading}
              prevalidate={prevalidateZipCode}
            />
          ) : null}
        </div>
      </div>
    );
  }
}
